import React, { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { Box, Tooltip } from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward,
  faThumbsUp,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { getStatusIcon } from '@/utils/statusIcon';
import { Campaign } from '../../types';

interface RulesEngineResults {
  mno: string;
  qualify: boolean;
  mnoReview: boolean;
  brandTier?: string;
  msgClass?: string;
}

interface CarrierItemProps {
  campaign: Campaign;
}

const CarrierItem: React.FC<CarrierItemProps> = ({ campaign }) => {
  const rulesEngineResults = useMemo<RulesEngineResults | null>(() => {
    if (!campaign.rulesEngineResultsJson) {
      return null;
    }
    try {
      return JSON.parse(campaign.rulesEngineResultsJson);
    } catch (error) {
      console.error('Failed to parse rulesEngineResultsJson:', error);
      return null;
    }
  }, [campaign.rulesEngineResultsJson]);

  const isTmobile = rulesEngineResults?.mno === 'T-Mobile';

  const operationStatusText =
    campaign.operationStatus?.toLowerCase() === 'approved'
      ? 'Registered'
      : campaign.operationStatus?.toLowerCase();

  const tcrStatusText =
    campaign.status === 'ACTIVE'
      ? 'Active'
      : campaign.status === 'EXPIRED'
      ? 'Deactivated'
      : campaign.status ?? '--';

  return (
    <Grid container className="" data-testid="carrierItem">
      <Grid item xs={1} data-testid="carrierItemMno">
        <div className="name flex-centered">
          <p>{rulesEngineResults?.mno ?? '--'}</p>
        </div>
      </Grid>
      <Grid item xs={2} data-testid="carrierItemQualify">
        <div className="label">
          <h3 className="paragraph">
            <Box
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
            >
              <Box margin={{ right: 'xxs' }}>
                <FontAwesomeIcon icon={faAward} size="xl" />
              </Box>
              <span>QUALIFY</span>
            </Box>
          </h3>
        </div>
        <div className="value">
          <p className="paragraph medium">
            {rulesEngineResults
              ? rulesEngineResults.qualify
                ? 'Yes'
                : 'No'
              : '--'}
          </p>
        </div>
      </Grid>
      <Grid item xs={2} data-testid="carrierItemMnoReview">
        <div className="label">
          <h3 className="paragraph">
            <Box
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
            >
              <Box margin={{ right: 'xxs' }}>
                <FontAwesomeIcon icon={faThumbsUp} size="xl" />
              </Box>
              <span>MNO REVIEW</span>
            </Box>
          </h3>
        </div>
        <div className="value">
          <p className="paragraph medium">
            {rulesEngineResults
              ? rulesEngineResults.mnoReview
                ? 'Yes'
                : 'No'
              : '--'}
          </p>
        </div>
      </Grid>
      <Grid
        item
        xs={2}
        style={{ marginLeft: 5, maxWidth: '15.3%' }}
        data-testid="carrierItemOperationStatus"
      >
        <div className="label" style={{ padding: 11 }}>
          <h3 className="paragraph">
            <span>CAMPAIGN STATUS</span>
          </h3>
        </div>
        <div className="value">
          <p className="paragraph medium">
            {campaign.operationStatus && (
              <Box
                flexDirection="row"
                justifyContent="center"
                alignItems="center"
              >
                <Box margin={{ right: 'xs' }}>
                  {getStatusIcon(campaign.operationStatus)}
                </Box>
                <span>{operationStatusText}</span>
                {campaign.operationStatus === 'SUSPENDED' &&
                  !!campaign.statusIssuerRole && (
                    <Tooltip
                      title={`Suspended by ${campaign.statusIssuerRole}`}
                      arrow
                      placement="bottom-end"
                    >
                      <span
                        style={{
                          marginLeft: '4px',
                          color: '#8D8F37',
                        }}
                        data-testid="carrierItemSuspendedTooltip"
                      >
                        <FontAwesomeIcon icon={faInfoCircle} />
                      </span>
                    </Tooltip>
                  )}
              </Box>
            )}
          </p>
        </div>
      </Grid>
      <Grid
        item
        xs={2}
        style={{ maxWidth: '13.9%' }}
        data-testid="carrierItemMnoDetails"
      >
        <div className="label" style={{ padding: 11 }}>
          <h3 className="paragraph">
            <span>
              {rulesEngineResults
                ? isTmobile
                  ? 'BRAND TIER'
                  : 'MESSAGE CLASS'
                : '--'}
            </span>
          </h3>
        </div>
        <div className="value">
          <p className="paragraph">
            {rulesEngineResults
              ? isTmobile
                ? rulesEngineResults.brandTier
                : rulesEngineResults.msgClass || 'N/A'
              : '--'}
          </p>
        </div>
      </Grid>
      <Grid
        item
        xs={2}
        style={{ maxWidth: '16.2%' }}
        data-testid="carrierItemDcaName"
      >
        <div className="label" style={{ padding: 11 }}>
          <h3 className="paragraph">
            <span>DCA NAME</span>
          </h3>
        </div>
        <div className="value">
          <p className="paragraph medium">
            {campaign.dcaName ? `DCA: ${campaign.dcaName}` : 'None'}
          </p>
        </div>
      </Grid>
      <Grid
        item
        xs={2}
        style={{ maxWidth: '12.3%' }}
        data-testid="carrierItemStatus"
      >
        <div className="label" style={{ padding: 11 }}>
          <h3 className="paragraph">
            <span>TCR STATUS</span>
          </h3>
        </div>
        <div className="value">
          <p className="paragraph medium">{tcrStatusText}</p>
        </div>
      </Grid>
    </Grid>
  );
};

export default CarrierItem;
